<template>
  <div class="book-page">
    <div class="wrap">
      <RouterLink v-if="projectId" class="back" :to="`/edit/${projectId}`">← Back to your roast</RouterLink>
      <h1>{{ rescheduleId ? 'Pick a new time' : 'Pick a time' }}</h1>
      <p class="sub">All times are Las Vegas time (PT). Meet your editor at booth A7.</p>

      <div v-if="loading" class="state">Loading times...</div>

      <div v-else-if="blocker" class="state card">
        <h2>{{ blocker.title }}</h2>
        <p>{{ blocker.text }}</p>
        <RouterLink class="btn-primary inline" :to="blocker.to">{{ blocker.cta }}</RouterLink>
      </div>

      <template v-else>
        <div class="days" role="tablist">
          <button
            v-for="d in days"
            :key="d.date"
            role="tab"
            class="day"
            :class="{ active: d.date === activeDate }"
            :aria-selected="d.date === activeDate"
            @click="activeDate = d.date"
          >
            <span class="day-name">{{ dayLabel(d.date) }}</span>
            <span class="day-count">{{ d.open ? `${d.open} open` : 'Full' }}</span>
          </button>
        </div>

        <div v-if="!hourGroups.length" class="state">No times left this day. Try another day.</div>

        <div v-for="g in hourGroups" :key="g.hour" class="hour">
          <div class="hour-label">{{ g.hour }}</div>
          <div class="grid">
            <button
              v-for="t in g.times"
              :key="t.startsAt"
              class="time"
              :class="{ selected: t.startsAt === selected }"
              :disabled="t.open === 0"
              @click="selected = t.startsAt"
            >
              {{ timeLabel(t.startsAt).replace(/ (AM|PM)$/, '') }}
            </button>
          </div>
        </div>

        <p class="reassure">Plans change. You can move or cancel from your confirmation email.</p>
      </template>
    </div>

    <div v-if="!loading && !blocker" class="bar">
      <div class="bar-inner">
        <div class="summary">
          <template v-if="selected">
            <strong>{{ shortSlotLabel(selected) }} PT</strong>
            <span>15 min consult · Booth A7</span>
          </template>
          <span v-else class="muted">Pick a time above</span>
        </div>
        <button class="btn-primary" :disabled="!selected || saving" @click="confirm">
          {{ saving ? 'Booking...' : rescheduleId ? 'Move my booking' : 'Book it' }}
        </button>
      </div>
      <p v-if="error" class="error">{{ error }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { track } from '../lib/analytics';
import {
  ConsultApiError,
  consultApi,
  dayLabel,
  hourLabel,
  shortSlotLabel,
  timeLabel,
  type ConsultDay,
  type ConsultTime,
} from '../lib/consults';
import { useAuthStore } from '../stores/auth';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const projectId = ref((route.query['project'] as string) || '');
const rescheduleId = (route.query['reschedule'] as string) || '';
const token = (route.query['token'] as string) || undefined;

const loading = ref(true);
const saving = ref(false);
const error = ref('');
const eventDays = ref<ConsultDay[]>([]);
const times = ref<ConsultTime[]>([]);
const activeDate = ref('');
const selected = ref((route.query['time'] as string) || '');
const blocker = ref<{ title: string; text: string; cta: string; to: string } | null>(null);

const days = computed(() =>
  eventDays.value.map(d => ({
    date: d.date,
    open: times.value.filter(t => t.date === d.date).reduce((sum, t) => sum + t.open, 0),
  }))
);

const hourGroups = computed(() => {
  const groups: { hour: string; times: ConsultTime[] }[] = [];
  for (const t of times.value.filter(t => t.date === activeDate.value)) {
    const hour = hourLabel(t.startsAt);
    const group = groups.at(-1);
    if (group?.hour === hour) group.times.push(t);
    else groups.push({ hour, times: [t] });
  }
  return groups;
});

async function load() {
  // New bookings need an account; reschedules can come from an email link with a token
  if (!rescheduleId && !authStore.isAuthenticated) {
    router.replace({ name: 'auth', query: { redirect: route.fullPath } });
    return;
  }
  try {
    if (!rescheduleId) {
      const me = await consultApi.me(projectId.value || undefined);
      if (me.booking) {
        blocker.value = {
          title: "You're already booked",
          text: `${shortSlotLabel(me.booking.startsAt)} PT at booth A7. You can change the time from your booking.`,
          cta: 'See my booking',
          to: `/author-nation/booking/${me.booking.id}?token=${me.booking.token}`,
        };
        return;
      }
      if (!me.eligible) {
        blocker.value = {
          title: 'Roast your chapter first',
          text: 'Consults are for authors who ran a chapter roast from our Author Nation page. It takes about a minute.',
          cta: 'Roast my chapter',
          to: '/author-nation',
        };
        return;
      }
      projectId.value = projectId.value || me.projectId || '';
    }
    const data = await consultApi.times();
    eventDays.value = data.event.days;
    times.value = data.times;
    const preselected = times.value.find(t => t.startsAt === selected.value && t.open > 0);
    if (!preselected) selected.value = '';
    activeDate.value = preselected?.date ?? days.value.find(d => d.open > 0)?.date ?? eventDays.value[0]?.date ?? '';
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Something broke. Try again in a sec.';
  } finally {
    loading.value = false;
  }
}

async function confirm() {
  if (!selected.value) return;
  saving.value = true;
  error.value = '';
  try {
    const { booking } = rescheduleId
      ? await consultApi.reschedule(rescheduleId, selected.value, token)
      : await consultApi.book(selected.value, projectId.value);
    track(rescheduleId ? 'consult_rescheduled' : 'consult_booked', { starts_at: booking.startsAt });
    router.push({ path: `/author-nation/booking/${booking.id}`, query: { token: booking.token } });
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Something broke. Try again in a sec.';
    if (e instanceof ConsultApiError && e.code === 'slot_taken') {
      // Refresh so the full time greys out
      const data = await consultApi.times().catch(() => null);
      if (data) times.value = data.times;
      selected.value = '';
    }
  } finally {
    saving.value = false;
  }
}

onMounted(() => {
  document.title = 'Pick a time | Twig';
  void load();
});
</script>

<style scoped>
.book-page {
  flex: 1;
  min-height: 100%;
  background: #fff;
  padding: 32px 16px 140px;
}
.wrap {
  max-width: 720px;
  margin: 0 auto;
}
.back {
  display: inline-block;
  font-size: 14px;
  color: var(--color-text-secondary);
  text-decoration: none;
  margin-bottom: 16px;
}
.back:hover {
  color: var(--color-text-primary);
}
h1 {
  font-size: 30px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0;
}
.sub {
  color: var(--color-text-secondary);
  margin: 6px 0 24px;
  font-size: 15px;
}
.days {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
  padding: 4px;
  background: var(--color-bg-tertiary);
  border-radius: 10px;
  margin-bottom: 24px;
}
.day {
  border: 0;
  background: transparent;
  border-radius: 8px;
  padding: 10px 6px;
  font: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}
.day.active {
  background: #fff;
  box-shadow: var(--shadow-sm);
}
.day-name {
  font-weight: 600;
  font-size: 14px;
}
.day-count {
  font-size: 12px;
  color: var(--color-text-muted);
}
.day.active .day-count {
  color: #1a5f8a;
}
.hour {
  margin-bottom: 18px;
}
.hour-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  margin-bottom: 8px;
}
.grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}
.time {
  border: 1px solid var(--color-border-secondary);
  background: #fff;
  border-radius: var(--border-radius);
  padding: 11px 0;
  font: inherit;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}
.time:hover:not(:disabled) {
  border-color: var(--color-accent);
  background: var(--color-accent-light);
}
.time.selected {
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: #fff;
}
.time:disabled {
  background: var(--color-bg-tertiary);
  color: var(--color-text-muted);
  text-decoration: line-through;
  cursor: not-allowed;
  border-color: transparent;
}
.reassure {
  color: var(--color-text-muted);
  font-size: 13px;
  margin-top: 24px;
}
.bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  border-top: 1px solid var(--color-border-primary);
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.05);
  padding: 14px 16px;
  z-index: 20;
}
.bar-inner {
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.summary {
  display: flex;
  flex-direction: column;
  font-size: 13px;
  color: var(--color-text-secondary);
}
.summary strong {
  font-size: 15px;
  color: var(--color-text-primary);
}
.muted {
  color: var(--color-text-muted);
}
.btn-primary {
  border: 0;
  border-radius: var(--border-radius);
  background: var(--color-accent);
  color: #fff;
  font: inherit;
  font-size: 16px;
  font-weight: 600;
  padding: 13px 28px;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
}
.btn-primary:hover:not(:disabled) {
  background: var(--color-accent-hover);
}
.btn-primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.btn-primary.inline {
  display: inline-block;
  margin-top: 8px;
}
.error {
  max-width: 720px;
  margin: 8px auto 0;
  color: var(--color-danger);
  font-size: 13px;
}
.state {
  padding: 32px 0;
  color: var(--color-text-secondary);
}
.card {
  padding: 28px;
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
  box-shadow: var(--shadow-md);
}
.card h2 {
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);
}
.card p {
  margin: 0 0 12px;
}
@media (max-width: 520px) {
  h1 {
    font-size: 26px;
  }
  .grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .day {
    padding: 8px 2px;
  }
  .day-name {
    font-size: 12.5px;
  }
}
</style>
